import { t } from '@lingui/core/macro'

import { OrderStatus } from '@/core/graphql/types'
import { getOrderCategory } from '@/entities/order'

export type TimelineStepState = 'done' | 'current' | 'upcoming'

export type TimelineStep = {
  key: 'requested' | 'accepted' | 'ready'
  label: string
  state: TimelineStepState
}

/**
 * Number of milestones a request has cleared on its way to a live consultation:
 * 0 = drafted, 1 = submitted, 2 = a doctor accepted, 3 = the consultation exists.
 */
function getOrderProgress(status: OrderStatus): number {
  switch (status) {
    case OrderStatus.Draft:
      return 0
    case OrderStatus.Pending:
      return 1
    case OrderStatus.Accepted:
    case OrderStatus.Confirmed:
    case OrderStatus.Rescheduled:
      return 2
    case OrderStatus.InProgress:
    case OrderStatus.ConsultationCreated:
    case OrderStatus.ExpertiseCreated:
    case OrderStatus.Completed:
      return 3
    default:
      // Cancelled / refused / no-show never cleared a milestone in this view.
      return 0
  }
}

/**
 * Builds the three-step progress track shown at the top of an order. When the
 * request is cancelled the steps are still returned (as context) alongside a
 * `cancelled` flag the header uses to switch to its terminal, red treatment.
 */
export function getOrderTimeline(status: OrderStatus): { steps: TimelineStep[]; cancelled: boolean } {
  const cancelled = getOrderCategory(status) === 'cancelled'
  const progress = getOrderProgress(status)

  const labels: Record<TimelineStep['key'], string> = {
    requested: t`Requested`,
    accepted: t`Accepted`,
    ready: t`Consultation ready`,
  }

  const keys: TimelineStep['key'][] = ['requested', 'accepted', 'ready']

  const steps = keys.map((key, index) => {
    let state: TimelineStepState = 'upcoming'
    if (index < progress) state = 'done'
    else if (index === progress) state = 'current'

    return { key, label: labels[key], state }
  })

  return { steps, cancelled }
}
